import {
  createEmbedBlockSchema,
  defineEmbedModel, // Import defineEmbedModel to create our custom model
  type EmbedYoutubeBlockProps,
} from '@blocksuite/affine-model';
import { BlockModel } from '@blocksuite/store';
import { literal } from 'lit/static-html.js';

import { PiqueEmbedYoutubeBlockService } from './youtube-embed-service';

export interface BlockTimestamp {
  timestamp: number;
  color: string;
}
// Default block imports from blocksuite
export type EmbedCardStyle =
  | 'horizontal'
  | 'horizontalThin'
  | 'list'
  | 'vertical'
  | 'cube'
  | 'cubeThick'
  | 'video'
  | 'figma'
  | 'html'
  | 'syncedDoc';

export const EmbedYoutubeStyles: EmbedCardStyle[] = ['video'] as const;

const defaultEmbedYoutubeProps: EmbedYoutubeBlockProps = {
  style: EmbedYoutubeStyles[0] ?? 'video',
  url: '',
  caption: null,
  image: null,
  title: null,
  description: null,
  creator: null,
  creatorUrl: null,
  creatorImage: null,
  videoId: null,
};

// Define a new type that extends EmbedYoutubeBlockProps with our new field
export type PiqueEmbedYoutubeBlockProps = EmbedYoutubeBlockProps & {
  blockTimestamps: Record<string, BlockTimestamp>;
  lastPlayingTime: number;
};

// Create a new model that extends BlockModel with our updated Props
export class PiqueEmbedYoutubeModel extends defineEmbedModel<PiqueEmbedYoutubeBlockProps>(
  // We use defineEmbedModel to create a new model class with our Props
  BlockModel
) {
  // Any additional methods or overrides can be added here if needed
}

const defaultPiqueEmbedYoutubeProps: PiqueEmbedYoutubeBlockProps = {
  ...defaultEmbedYoutubeProps,
  blockTimestamps: {},
  lastPlayingTime: 0,
};

export const PiqueEmbedYoutubeBlockSchema = createEmbedBlockSchema({
  name: 'pique-youtube',
  version: 1,
  toModel: () => new PiqueEmbedYoutubeModel(),
  props: (): PiqueEmbedYoutubeBlockProps => defaultPiqueEmbedYoutubeProps,
});

// Define the block spec
export const PiqueYoutubeEmbedBlockSpec = {
  schema: PiqueEmbedYoutubeBlockSchema,
  service: PiqueEmbedYoutubeBlockService,
  view: {
    component: literal`pique-youtube-embed-block`,
  },
};

/* eslint-disable @typescript-eslint/no-namespace, @typescript-eslint/prefer-namespace-keyword */
declare global {
  namespace BlockSuite {
    interface BlockModels {
      'affine:embed-pique-youtube': PiqueEmbedYoutubeModel;
    }
    interface EdgelessBlockModelMap {
      'affine:embed-pique-youtube': PiqueEmbedYoutubeModel;
    }
  }
}
